import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  AncillaryContent,
  SearchTrip,
  ViewTripHeader
} from '@com-delta-idp/mnx-capability-trip-management-ui';
import {
  ApplicationConfig,
  DashboardResponse,
  MyTripAem,
  UserDataService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { EMPTY } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { getAncillaryContent } from '../adapters/ancillary.adapter';
import { getSearchContent } from '../adapters/search.adapter';
import { adaptContent } from '../adapters/upcoming-header.adapter';
import { MostUpcomingTrip } from '../models/interface/upcoming-trip.interface';
import { ConfigService } from '../services/config.service';
import { MyTripsService } from '../services/my-trips.service';

@Injectable({
  providedIn: 'root'
})
export class MyTripFacadeHelper {
  searchContent = signal<SearchTrip>({} as SearchTrip);
  ancillaryContent = signal<AncillaryContent>({} as AncillaryContent);

  protected userData = inject(UserDataService);

  constructor(
    private tripsService: MyTripsService,
    private config: ConfigService,
    private appConfig: ApplicationConfig
  ) {}

  updateSearchAndAncillaryContent(
    staticContent: MyTripAem,
    tripDetails: MostUpcomingTrip,
    nameLengthConfig?: boolean
  ): void {
    const tripCount = tripDetails?.totalTrips || '';
    const myTripSearchContent: SearchTrip = getSearchContent(
      staticContent,
      this.tripsService.url$.value,
      tripCount,
      nameLengthConfig
    );
    this.searchContent.set(myTripSearchContent);
    this.ancillaryContent.set(
      getAncillaryContent(staticContent.ancillaryLinks, tripCount)
    );
  }

  updateCurrentType(currentType: string): void {
    const currentContent = this.searchContent();
    this.searchContent.set({
      ...currentContent,
      currentType,
      submitClicked: false
    });
  }

  displayUpcomingTripDetails(
    response: MostUpcomingTrip,
    staticContent: MyTripAem,
    displayFindTripLink: WritableSignal<boolean>,
    displayTripDetails: WritableSignal<boolean>,
    adaptedTripDetails: WritableSignal<ViewTripHeader | null>
  ): void {
    if (Number(response.totalTrips) > 0) {
      displayTripDetails.set(true);
      if (staticContent) {
        adaptedTripDetails.set(adaptContent(response, staticContent));
        this.updateSearchAndAncillaryContent(staticContent, response);
      }
    } else if (response.errors?.length) {
      displayFindTripLink.set(true);
      displayTripDetails.set(false);
    }
  }

  callUpcomingTripsApi(
    dashBoardResponse: DashboardResponse,
    mostRecentTripsData: WritableSignal<MostUpcomingTrip>,
    displayErrorMessage: WritableSignal<boolean>,
    showMytrip: WritableSignal<boolean>,
    onSuccess: (response: MostUpcomingTrip) => void,
    onError: () => void,
    onloadCall = false
  ): void {
    this.config
      .callMostUpcomingTrips({
        skyMileNumber: dashBoardResponse.smNumber,
        numberOfTrips: '1'
      })
      .pipe(
        map((response) => {
          if (response.errors?.length) {
            onSuccess(response);
            return null;
          }
          if (!response.trip?.detailsLink || !response.trip?.firstName) {
            throw new Error();
          }
          return response;
        }),
        filter((response): response is MostUpcomingTrip => response !== null),
        catchError(() => {
          displayErrorMessage.set(true);
          onError();
          return EMPTY;
        })
      )
      .subscribe((response) => {
        mostRecentTripsData.set(response);
        onSuccess(response);
        this.handleCheckinEligibility(response, showMytrip, onloadCall);
      });
  }

  private handleCheckinEligibility(
    response: MostUpcomingTrip,
    showMytrip: WritableSignal<boolean>,
    onloadCall: boolean
  ): void {
    if (this.isCheckinEligible(response, onloadCall)) {
      showMytrip.set(true);
    }
  }

  private isCheckinEligible(
    response: MostUpcomingTrip,
    onloadCall: boolean
  ): boolean {
    const appName = this.appConfig.get('APP');
    return !!(
      onloadCall &&
      response.trip?.checkinEligible &&
      !response.trip?.checkedIn &&
      appName === 'homepage'
    );
  }
}
